import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Navbar from "../Navbar/LeadNavbar";
import { RootState, RootDispatch } from "../../store";
import { extractRoles } from "../../store/slices/commonSlice";
import {
  fetchAssignee,
  fetchLeadLogCount,
  toggleColumnVisibility,
  removeColumnFromState,
  getStatesData,
  setColumnOrder,
  saveUserColumnPreferences,
} from "../../store/slices/kanbanSlice";
import dayjs from "dayjs";
import {
  ISourceType,
  IStateDistrict,
  IColumn,
} from "../../models/LeadData.model";
import OptimizedKanbanBoard from "../OptimizedKanban/OptimizedKanbanBoard";
import AddLead from "../Lead/AddLead";
import ColumnVisibilityModal from "./ColumnVisibilityModal";
import { deleteStatusColumn } from "../../store/slices/statusColumnSlice";
import toast from "react-hot-toast";
import { getLeadAssigneeRoleWise } from "../../store/slices/leadSlice";
import {
  clearFilters,
  setFilters,
} from "../../store/slices/kanbanFiltersSlice";
import Select from "react-select";
import OptimizedKanbanList from "./OptimizedKanbanList";

const KanbanWrapperBoard: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();

  const assigneeRoleWise = useSelector(
    (state: RootState) => state?.leadSlice?.assignees
  );
  const leadLogCount = useSelector(
    (state: RootState) => state.kanban.todayLeadLogCount
  );
  const columns = useSelector((state: RootState) => state.kanban.columns);
  const visibleColumns = useSelector(
    (state: RootState) => state.kanban.visibleColumns
  );
  const statesData = useSelector((state: RootState) => state.kanban.statesData);

  const filters = useSelector((s: RootState) => s.kanbanFilters);

  const [, setSearchOnEnter] = useState<string | null>(null);
  const [pincodeTerm, setPincodeTerm] = useState(filters.pincode || "");
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [view, setView] = useState<"kanban" | "list">("kanban");

  const handleColumnOrderChange = async (newColumns: IColumn[]) => {
    try {
      setColumnOrder(newColumns);

      const columnPreferences = newColumns.map((col) => ({
        columnId: col.id,
        isVisible: visibleColumns[col.id],
      }));

      setColumnOrder(newColumns);

      dispatch(saveUserColumnPreferences(columnPreferences));
    } catch (error) {
      console.error("Failed to save column preferences:", error);
    }
  };

  const findState = statesData.find((s) => s.state === (filters.state || ""));

  const categoryOptions = [
    { value: "epc", label: "EPC" },
    { value: "trader", label: "Trader" },
    { value: "distributor", label: "Distributor" },
  ];

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "34px",
      height: "34px",
      borderColor: "#d1d5db",
      backgroundColor: "#f9fafb",
      fontSize: "0.75rem",
    }),
    menu: (base: any) => ({ ...base, fontSize: "0.75rem", zIndex: 50 }),
  };

  const sourceTypeOptions = Object.values(ISourceType).map((type) => ({
    value: type,
    label: type,
  }));

  const assigneeOptions =
    assigneeRoleWise?.map((assignee) => ({
      value: assignee?._id,
      label: assignee?.username,
    })) || [];

  const stateOptions = [
    { value: "empty", label: "Empty States" },
    ...statesData.map((s) => ({
      value: s.state.toLowerCase(),
      label: s.state.replace(/\b\w/g, (c) => c.toUpperCase()),
    })),
  ];

  const districtOptions = findState
    ? [
        { value: "empty", label: "Empty Districts" },
        ...findState.districts.map((d) => ({
          value: d.toLowerCase(),
          label: d.replace(/\b\w/g, (c) => c.toUpperCase()),
        })),
      ]
    : [];

  function sanitizeFilters(raw: typeof filters) {
    const {
      assignee,
      sourceType,
      date,
      state,
      district,
      pincode,
      searchQuery,
      category,
    } = raw;
    return {
      assignee: assignee || undefined,
      sourceType: sourceType ?? undefined,
      date: date ?? undefined,
      state: state ?? undefined,
      district: district ?? undefined,
      pincode: pincode ?? undefined,
      searchQuery: searchQuery || undefined,
      category: category || undefined,
    };
  }

  useEffect(() => {
    dispatch(extractRoles());
    dispatch(fetchAssignee());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getLeadAssigneeRoleWise());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchLeadLogCount(filters.date || undefined));
  }, [dispatch, filters.date]);

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  const resetFilters = () => {
    dispatch(clearFilters());
    dispatch(fetchLeadLogCount());
  };

  const handleToggleColumn = (columnId: string) => {
    dispatch(toggleColumnVisibility(columnId));
  };

  const handleDeleteColumn = async (columnId: string) => {
    try {
      await dispatch(deleteStatusColumn(columnId)).unwrap();
      dispatch(removeColumnFromState(columnId));
    } catch (error) {
      toast.error("Error deleting column");
    }
  };

  return (
    <>
      <Navbar
        setIsModalOpen={setIsColumnModalOpen}
        isModalOpen={isColumnModalOpen}
        onSearchEnter={(q) => {
          dispatch(setFilters({ searchQuery: q || "" }));
          setSearchOnEnter(q || null);
        }}
        onViewChange={(view) => setView(view)}
        filters={sanitizeFilters(filters)}
      />

      {/* Filters Section */}
      <div className="p-4 bg-white shadow-md rounded-lg grid grid-cols-1 md:grid-cols-9 gap-4">
        {/* Source Type Filter */}
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-700 mb-1">
            Source Type
          </label>
          <Select
            options={sourceTypeOptions}
            value={
              sourceTypeOptions.find((o) => o.value === filters.sourceType) ||
              null
            }
            onChange={(opt) =>
              dispatch(setFilters({ sourceType: opt?.value || null }))
            }
            isClearable
            placeholder="Select..."
            styles={customSelectStyles}
          />
        </div>

        {/* Assignee Filter */}
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-700 mb-1">
            Assignee
          </label>
          <Select
            options={assigneeOptions}
            value={
              assigneeOptions.find((o) => o.value === filters.assignee) || null
            }
            onChange={(opt) =>
              dispatch(setFilters({ assignee: opt?.value || "" }))
            }
            isClearable
            placeholder="Select..."
            styles={customSelectStyles}
          />
        </div>

        {/* State Filter */}
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-700 mb-1">
            State
          </label>
          <Select
            options={stateOptions}
            value={stateOptions.find((o) => o.value === filters.state) || null}
            onChange={(opt) =>
              dispatch(setFilters({ state: opt?.value || null }))
            }
            isClearable
            placeholder="Select..."
            styles={customSelectStyles}
          />
        </div>

        {/* District Filter */}
        {findState && (
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-700 mb-1">
              District
            </label>
            <Select
              options={districtOptions}
              value={
                districtOptions.find((o) => o.value === filters.district) ||
                null
              }
              onChange={(opt) =>
                dispatch(setFilters({ district: opt?.value || null }))
              }
              isClearable
              placeholder="Select..."
              styles={customSelectStyles}
            />
          </div>
        )}

        {/* Category Filter */}
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-700 mb-1">
            Category
          </label>
          <Select
            options={categoryOptions}
            value={
              categoryOptions.find((c) => c.value === filters.category) || null
            }
            onChange={(selectedOption) =>
              dispatch(
                setFilters({
                  category: selectedOption ? selectedOption.value : null,
                })
              )
            }
            isClearable
            placeholder="Select..."
            className="text-xs"
            styles={customSelectStyles}
          />
        </div>

        {/* Pincode Filter */}
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-700 mb-1">
            Pincode
          </label>
          <input
            type="text"
            value={pincodeTerm}
            onChange={(e) => setPincodeTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                dispatch(setFilters({ pincode: pincodeTerm.trim() || null }));
              }
            }}
            className="mt-1 p-2 text-xs rounded-md border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {/* Date Filter */}
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={filters.date || ""}
            onChange={(e) =>
              dispatch(setFilters({ date: e.target.value || null }))
            }
            className="mt-1 p-2 text-xs rounded-md border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {/* Reset Button */}
        <div className="flex items-end">
          <button
            onClick={resetFilters}
            className="px-4 py-2 text-xs bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reset Filters
          </button>
        </div>

        {/* Lead Count Display */}
        <div className="flex justify-end items-end col-span-2">
          <div className="flex items-center bg-blue-50 px-3 py-2 rounded-full">
            <span className="text-xs font-medium text-blue-700">
              {filters.date
                ? dayjs(filters.date).format("DD MMM YYYY")
                : "Today"}
              's Leads Added:
              <span className="font-bold text-blue-800 ml-1">
                {leadLogCount}
              </span>
            </span>
          </div>
        </div>
      </div>

      <AddLead />

      {view === "kanban" ? (
        <OptimizedKanbanBoard sanitizeFilters={sanitizeFilters} />
      ) : (
        <OptimizedKanbanList sanitizeFilters={sanitizeFilters} />
      )}

      {isColumnModalOpen && (
        <ColumnVisibilityModal
          columns={Object.values(columns)}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
          onDeleteColumn={handleDeleteColumn}
          onClose={() => setIsColumnModalOpen(false)}
          setModalOpen={setIsColumnModalOpen}
          onColumnOrderChange={handleColumnOrderChange}
        />
      )}
    </>
  );
};

export default KanbanWrapperBoard;
